import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Copy,
  CheckCircle,
  UploadSimple,
  Bank,
  Receipt,
  Spinner,
  Warning,
} from '@phosphor-icons/react';
import { trackEvent } from '../lib/metaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRANSFER_INFO = {
  alias: 'agencymarket.off',
  holder: 'Juan Manuel Ovejero',
};

const formatARS = (v) => `$${(v || 0).toLocaleString('es-AR')}`;

export const PaymentTransferPanel = ({ order, onComplete, onReceiptUploaded }) => {
  const [copiedField, setCopiedField] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const fileInputRef = useRef(null);

  // 🎯 Meta Pixel: AddPaymentInfo — el usuario llegó a la pantalla donde ve
  // los datos de transferencia (equivalente a "añadir info de pago"). Idempotente
  // por order.id para no re-fire si el componente se re-monta.
  useEffect(() => {
    if (!order?.id) return;
    try {
      const key = `fbq_addpay_${order.id}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) {
      /* no-op */
    }
    const value = Number(order.total_price_ars ?? order.total_ars ?? order.price_ars ?? 0);
    trackEvent('AddPaymentInfo', {
      content_category: order.platform,
      content_ids: order.service_id ? [order.service_id] : undefined,
      currency: 'ARS',
      value,
      order_id: order.id,
    });
  }, [order?.id, order?.platform, order?.service_id, order?.total_price_ars, order?.total_ars, order?.price_ars]);

  const copy = async (value, field) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      toast.success('Copiado');
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato no permitido (solo JPG, PNG o WEBP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagen demasiado grande (máximo 5MB)');
      return;
    }

    setUploading(true);
    setAnalysis(null);

    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(
        `${API_URL}/api/orders/${order.id}/upload-receipt`,
        fd,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setAnalysis(data.analysis);
      toast.success('¡Pedido realizado! Comprobante recibido.');
      if (onReceiptUploaded) {
        onReceiptUploaded(data.analysis);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const code = typeof detail === 'object' ? detail.code : null;
      if (code === 'RECEIPT_NOT_TO_OWNER') {
        toast.error(detail.message, { duration: 8000 });
      } else {
        const msg =
          typeof detail === 'string'
            ? detail
            : detail?.message || 'Error al subir comprobante';
        toast.error(msg);
      }
    } finally {
      setUploading(false);
    }
  };

  // WhatsApp de soporte/recargas — NUNCA para enviar comprobantes.
  // Los clientes se confunden y mandan la foto por chat en vez de subirla.

  return (
    <div className="space-y-6">
      {/* ⚠️ Recordatorio permanente arriba del panel: el pedido NO está pagado */}
      <div className="rounded-xl border-2 border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3" data-testid="unpaid-warning">
        <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
          <Warning size={22} weight="fill" className="text-red-500" />
        </div>
        <div className="text-sm">
          <div className="font-bold text-red-500 mb-0.5">Tu pedido AÚN NO está pagado</div>
          <p className="text-foreground/90">
            Transferí el monto exacto al alias de abajo y subí el comprobante acá mismo. Recién cuando el
            pago se verifique se activa tu pedido.
          </p>
        </div>
      </div>

      <Card className="p-6 border-border" data-testid="transfer-info-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0066FF] to-[#00E676] flex items-center justify-center">
            <Bank size={20} weight="fill" className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold">Datos para transferir</h3>
            <p className="text-xs text-muted-foreground">Transferí desde tu billetera o banco</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">Alias</div>
              <div className="font-mono font-bold text-base sm:text-lg truncate">
                {TRANSFER_INFO.alias}
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copy(TRANSFER_INFO.alias, 'alias')}
              data-testid="copy-alias"
            >
              {copiedField === 'alias' ? (
                <CheckCircle size={18} weight="fill" className="text-[#00E676]" />
              ) : (
                <Copy size={18} />
              )}
            </Button>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div>
              <div className="text-xs text-muted-foreground">Titular</div>
              <div className="font-medium">{TRANSFER_INFO.holder}</div>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
            <div>
              <div className="text-xs text-muted-foreground">Monto exacto</div>
              <div className="font-bold text-xl text-primary">{formatARS(order.price)}</div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copy(String(order.price), 'amount')}
              data-testid="copy-amount"
            >
              {copiedField === 'amount' ? (
                <CheckCircle size={18} weight="fill" className="text-[#00E676]" />
              ) : (
                <Copy size={18} />
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Upload comprobante */}
      <Card className="p-6 border-border" data-testid="receipt-upload-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00E676] to-emerald-600 flex items-center justify-center">
            <Receipt size={20} weight="fill" className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold">Adjuntá tu comprobante</h3>
            <p className="text-xs text-muted-foreground">
              Subílo y confirmamos tu pedido al instante
            </p>
          </div>
        </div>

        {!analysis && !uploading && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full py-8 border-2 border-dashed border-border hover:border-[#00E676]/50 hover:bg-muted/50 rounded-xl text-center transition-all"
            data-testid="upload-receipt-button"
          >
            <UploadSimple size={32} weight="duotone" className="mx-auto mb-2 text-muted-foreground" />
            <div className="font-medium mb-1">Tocá para subir el comprobante</div>
            <div className="text-xs text-muted-foreground">JPG, PNG o WEBP · máx 5MB</div>
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleFileSelected}
          data-testid="receipt-file-input"
        />

        {uploading && (
          <div className="py-8 text-center" data-testid="receipt-uploading">
            <Spinner size={32} className="mx-auto mb-2 text-primary animate-spin" />
            <div className="font-medium">Procesando tu comprobante...</div>
            <div className="text-xs text-muted-foreground mt-1">No cierres esta ventana</div>
          </div>
        )}

        {analysis && (
          <div className="space-y-4" data-testid="receipt-analysis-result">
            <div className="p-5 bg-gradient-to-br from-[#00E676]/10 to-[#0066FF]/10 border border-[#00E676]/30 rounded-2xl">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-[#00E676] flex items-center justify-center shrink-0 shadow-lg shadow-[#00E676]/30">
                  <CheckCircle size={28} weight="fill" className="text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-lg mb-1 text-[#00E676]">¡Pedido realizado!</div>
                  <p className="text-sm text-foreground mb-3">
                    Tu comprobante fue recibido y tu pedido ya está en cola de procesamiento.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2 p-2.5 bg-background/60 rounded-lg">
                      <span className="text-[#00E676] mt-0.5">⚡</span>
                      <span>
                        <strong>Te llega en 0-30 min</strong> en caso de no haber demoras.
                      </span>
                    </div>
                    <div className="flex items-start gap-2 p-2.5 bg-background/60 rounded-lg">
                      <span className="text-yellow-500 mt-0.5">⏱</span>
                      <span>
                        Si el sistema está colapsado puede demorar entre <strong>3-6 hs</strong>.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="w-full"
              data-testid="reupload-receipt-button"
            >
              <UploadSimple size={16} className="mr-2" />
              Subir otro comprobante
            </Button>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            📎 El comprobante <strong className="text-foreground">se sube solamente acá arriba</strong>.
            <br />
            El WhatsApp <strong className="text-foreground">+54 11 7174-0757</strong> es únicamente
            para <strong className="text-foreground">soporte y recargas</strong>.
          </p>
        </div>
      </Card>

      {onComplete && (
        <Button variant="ghost" onClick={onComplete} className="w-full">
          Volver al inicio
        </Button>
      )}
    </div>
  );
};
